import type { NotificationInboxItem } from '~/services/api'
import { formatApiDateTime } from '~/utils/formatApiDateTime'
import type { PresentationInstruction } from './PresentationInstruction'
import type { PresentationScalar } from './PresentationScalar'
import type { ResolverMap } from './ResolverMap'
import type { TranslateFn } from './TranslateFn'
import { resolveCode } from './resolveCode'
import type { NotificationDisplay } from './NotificationDisplay'

// --- NotificationTemplateCode: in-app notification bodies rendered from a
// backend-supplied template code + params, replacing pre-rendered title/body
// strings. Codes are added here as their producers land. ---

type NotificationTemplateCode = 'ORDER_UPDATES' | 'NEW_ORDER'

type OrderUpdatesReason = 'accepted' | 'declined'
type NewOrderReason = 'with_requested_date' | 'without_requested_date'

const notificationTitleByCode = {
  ORDER_UPDATES: 'presentation.notifications.orderUpdatesTitleGeneric',
  NEW_ORDER: 'presentation.notifications.newOrderTitle'
} satisfies Record<NotificationTemplateCode, string>

const notificationTitleByReason: Partial<Record<NotificationTemplateCode, Record<string, string>>> =
  {
    ORDER_UPDATES: {
      accepted: 'presentation.notifications.orderUpdatesTitleAccepted',
      declined: 'presentation.notifications.orderUpdatesTitleDeclined'
    } satisfies Record<OrderUpdatesReason, string>
  }

const notificationTitleMap: ResolverMap<NotificationTemplateCode> = {
  byCode: notificationTitleByCode,
  byReason: notificationTitleByReason,
  genericKey: 'presentation.notifications.orderUpdatesTitleGeneric'
}

const notificationBodyByCode = {
  ORDER_UPDATES: 'presentation.notifications.orderUpdatesBodyGeneric',
  NEW_ORDER: 'presentation.notifications.newOrderBodyWithoutDate'
} satisfies Record<NotificationTemplateCode, string>

const notificationBodyByReason: Partial<Record<NotificationTemplateCode, Record<string, string>>> =
  {
    ORDER_UPDATES: {
      accepted: 'presentation.notifications.orderUpdatesBodyAccepted',
      declined: 'presentation.notifications.orderUpdatesBodyDeclined'
    } satisfies Record<OrderUpdatesReason, string>,
    NEW_ORDER: {
      with_requested_date: 'presentation.notifications.newOrderBodyWithDate',
      without_requested_date: 'presentation.notifications.newOrderBodyWithoutDate'
    } satisfies Record<NewOrderReason, string>
  }

const notificationBodyMap: ResolverMap<NotificationTemplateCode> = {
  byCode: notificationBodyByCode,
  byReason: notificationBodyByReason,
  genericKey: 'presentation.notifications.orderUpdatesBodyGeneric'
}

const notificationActionViewKeyByCode = {
  ORDER_UPDATES: 'presentation.notifications.orderUpdatesActionView',
  NEW_ORDER: 'presentation.notifications.newOrderActionView'
} satisfies Record<NotificationTemplateCode, string>

function isNotificationTemplateCode(
  code: string | null | undefined
): code is NotificationTemplateCode {
  return code === 'ORDER_UPDATES' || code === 'NEW_ORDER'
}

// The inbox API is a flat shape where every field is always present:
// template-code fields (template_code/params/reason/action) are null when the
// notification uses the pre-rendered title/body/action_label fields, and vice
// versa. `template_code !== null` is the one correct discriminant.
type NotificationParams = NotificationInboxItem['params']

// The inbox API types `params` values as nullable (the backend accepts a
// scalar-or-null dict), but PresentationInstruction params only carry
// interpolatable scalars, so null entries and a null params object are
// dropped here at the boundary.
function toPresentationParams(params: NotificationParams): Record<string, PresentationScalar> {
  const result: Record<string, PresentationScalar> = {}

  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== null) result[key] = value
  }

  return result
}

// NEW_ORDER's `requested_at` param is a raw ISO 8601 timestamp (in the
// store's timezone offset) rather than a server-formatted date label, so the
// browser formats it locally here before it reaches the i18n interpolation
// step, and selects the with/without-date body variant via `reason`.
function buildNewOrderInstruction(
  params: NotificationParams
): PresentationInstruction<NotificationTemplateCode> {
  const requestedAt =
    typeof (params ?? {})['requested_at'] === 'string'
      ? ((params ?? {})['requested_at'] as string)
      : null

  return {
    code: 'NEW_ORDER',
    params: {
      ...toPresentationParams(params),
      requested_date_label: requestedAt !== null ? formatApiDateTime(requestedAt) : ''
    },
    reason: requestedAt !== null ? 'with_requested_date' : 'without_requested_date'
  }
}

function buildNotificationInstruction(
  notification: NotificationInboxItem
): PresentationInstruction<NotificationTemplateCode> | null {
  const templateCode = notification.template_code
  if (!isNotificationTemplateCode(templateCode)) return null

  if (templateCode === 'NEW_ORDER') {
    return buildNewOrderInstruction(notification.params)
  }

  return {
    code: templateCode,
    params: toPresentationParams(notification.params),
    ...(notification.reason !== null ? { reason: notification.reason } : {})
  }
}

// Resolves the title/body/action-label a notification should display,
// covering both branches of the flat inbox row: a `template_code` row
// (resolved through the locale-backed maps above) and a pre-rendered row
// (title/body/action_label fields, read as-is).
export function resolveNotificationDisplay(
  notification: NotificationInboxItem,
  t: TranslateFn
): NotificationDisplay {
  if (notification.template_code !== null) {
    const instruction = buildNotificationInstruction(notification)
    if (instruction === null) return { title: '', body: '', actionLabel: '' }

    return {
      title: resolveCode(instruction, notificationTitleMap, t),
      body: resolveCode(instruction, notificationBodyMap, t),
      actionLabel:
        notification.action?.code === 'OPEN_ENTITY'
          ? t(notificationActionViewKeyByCode[instruction.code])
          : ''
    }
  }

  return {
    title: notification.title ?? '',
    body: notification.body ?? '',
    actionLabel: notification.action_label ?? ''
  }
}
